import { useState, useEffect, useCallback } from 'react';
import { 
  Kanban, Plus, MagnifyingGlass, Funnel, Calendar, User, Tag, Trash, 
  PencilSimple, DotsThree, CaretDown, CheckCircle, Clock, Warning,
  CircleNotch, X, Flag, FileText, Download, PencilLine
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { id: 'todo', title: 'To Do', color: 'bg-gray-500' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-500' },
  { id: 'review', title: 'Review', color: 'bg-yellow-500' },
  { id: 'done', title: 'Done', color: 'bg-green-500' }
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  { id: 'medium', label: 'Medium', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'high', label: 'High', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { id: 'urgent', label: 'Urgent', color: 'text-red-400', bg: 'bg-red-500/20' }
];

export default function TasksPage() {
  const { token, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [boards, setBoards] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [editingBoardId, setEditingBoardId] = useState(null);  // null = create mode, id = edit mode
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState('all');
  const [draggedTask, setDraggedTask] = useState(null);
  
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    tags: [],
    board_id: ''
  });
  
  const [boardForm, setBoardForm] = useState({ name: '', description: '', color: '#3b82f6' });
  const [tagInput, setTagInput] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'completed'
  // Summary-PDF filter state (does NOT yet filter the kanban view itself; only the PDF export.)
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [dueStartDate, setDueStartDate] = useState('');
  const [dueEndDate, setDueEndDate] = useState('');
  const [showSummaryFilters, setShowSummaryFilters] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, boardsRes, statsRes, usersRes] = await Promise.all([
        fetch(`${API}/api/tasks${selectedBoard !== 'all' ? `?board_id=${selectedBoard}` : ''}`, { headers }),
        fetch(`${API}/api/tasks/boards`, { headers }),
        fetch(`${API}/api/tasks/stats`, { headers }),
        fetch(`${API}/api/hr/employees`, { headers })
      ]);
      
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (boardsRes.ok) setBoards(await boardsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const emps = await usersRes.json();
        setUsers(emps.map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}` })));
      }
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [token, selectedBoard]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...taskForm,
        board_id: taskForm.board_id || null,
        assigned_to: taskForm.assigned_to || null
      };
      
      const url = selectedTask ? `${API}/api/tasks/${selectedTask.id}` : `${API}/api/tasks`;
      const method = selectedTask ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(selectedTask ? 'Task updated' : 'Task created');
        setShowTaskModal(false);
        resetTaskForm();
        fetchData();
      } else {
        toast.error('Failed to save task');
      }
    } catch (err) {
      toast.error('Error saving task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      const res = await fetch(`${API}/api/tasks/${taskId}`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success('Task deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Error deleting task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/tasks/${taskId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers
      });
      if (res.ok) {
        // Optimistic local update so the card moves columns immediately,
        // then refetch so completed_at / completed_by_name stamps (or their
        // clearing on revert) are reflected on the card chip + Completed view.
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
        toast.success('Status updated');
        fetchData();
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    try {
      const isEdit = !!editingBoardId;
      const url = isEdit
        ? `${API}/api/tasks/boards/${editingBoardId}`
        : `${API}/api/tasks/boards`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(boardForm)
      });
      if (res.ok) {
        toast.success(isEdit ? 'Board updated' : 'Board created');
        setShowBoardModal(false);
        setEditingBoardId(null);
        setBoardForm({ name: '', description: '', color: '#3b82f6' });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save board');
      }
    } catch (err) {
      toast.error(`Error ${editingBoardId ? 'updating' : 'creating'} board`);
    }
  };

  const handleEditBoard = (board) => {
    setEditingBoardId(board.id);
    setBoardForm({
      name: board.name || '',
      description: board.description || '',
      color: board.color || '#3b82f6',
    });
    setShowBoardModal(true);
  };

  // Branded PDF summary download
  const handleDownloadSummaryPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBoard && selectedBoard !== 'all') params.set('board_id', selectedBoard);
      if (priorityFilter && priorityFilter !== 'all') params.set('priority', priorityFilter);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (assigneeFilter && assigneeFilter !== 'all') params.set('assigned_to', assigneeFilter);
      if (dueStartDate) params.set('start_date', dueStartDate);
      if (dueEndDate) params.set('end_date', dueEndDate);
      const url = `${API}/api/tasks/summary/pdf?${params.toString()}`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        toast.error('Failed to generate tasks summary PDF');
        return;
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      // Open in new tab for view/print AND offer download
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      toast.success('Tasks summary opened');
    } catch (err) {
      toast.error('Failed to fetch tasks summary');
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (!confirm('Delete this board? Tasks will be moved to unassigned.')) return;
    try {
      const res = await fetch(`${API}/api/tasks/boards/${boardId}`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success('Board deleted');
        if (selectedBoard === boardId) setSelectedBoard('all');
        fetchData();
      }
    } catch (err) {
      toast.error('Error deleting board');
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: '', description: '', status: 'todo', priority: 'medium',
      assigned_to: '', due_date: '', tags: [], board_id: ''
    });
    setSelectedTask(null);
  };

  const openEditTask = (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      assigned_to: task.assigned_to || '',
      due_date: task.due_date || '',
      tags: task.tags || [],
      board_id: task.board_id || ''
    });
    setShowTaskModal(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !taskForm.tags.includes(tagInput.trim())) {
      setTaskForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setTaskForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleDragStart = (task) => setDraggedTask(task);
  const handleDragEnd = () => setDraggedTask(null);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (status) => {
    if (draggedTask && draggedTask.status !== status) {
      handleStatusChange(draggedTask.id, status);
    }
    setDraggedTask(null);
  };

  const getTasksByStatus = (status) => tasks.filter(t => t.status === status);
  
  const getPriorityStyle = (priority) => PRIORITIES.find(p => p.id === priority) || PRIORITIES[1];
  
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Kanban className="w-7 h-7 text-primary" />
            Tasks & Workflow
          </h1>
          <p className="text-muted-foreground mt-1">Manage tasks with Kanban boards</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowSummaryFilters(s => !s)} data-testid="toggle-summary-filters">
            <FileText className="w-4 h-4 mr-2" />
            Summary PDF
          </Button>
          {boards.length > 0 && selectedBoard !== 'all' && (() => {
            const b = boards.find(x => x.id === selectedBoard);
            if (!b) return null;
            return (
              <Button variant="outline" onClick={() => handleEditBoard(b)} data-testid="edit-board-btn">
                <PencilLine className="w-4 h-4 mr-2" />
                Edit Board
              </Button>
            );
          })()}
          <Button variant="outline" onClick={() => { setEditingBoardId(null); setBoardForm({ name:'', description:'', color:'#3b82f6' }); setShowBoardModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            New Board
          </Button>
          <Button onClick={() => { resetTaskForm(); setShowTaskModal(true); }} data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Summary PDF filter row (collapsed by default; toggled by Summary PDF button) */}
      {showSummaryFilters && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Tasks Summary PDF — Filters
            </h3>
            <Button size="sm" onClick={handleDownloadSummaryPdf} data-testid="download-summary-pdf">
              <Download className="w-4 h-4 mr-2" />
              View / Download PDF
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <select className="w-full mt-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} data-testid="filter-priority">
                <option value="all">All</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="w-full mt-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status">
                <option value="all">All</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Assigned To</Label>
              <select className="w-full mt-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm" value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} data-testid="filter-assignee">
                <option value="all">All</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Due From</Label>
              <Input type="date" value={dueStartDate} onChange={e => setDueStartDate(e.target.value)} data-testid="filter-due-from" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Due To</Label>
              <Input type="date" value={dueEndDate} onChange={e => setDueEndDate(e.target.value)} data-testid="filter-due-to" className="mt-1" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            These filters only affect the generated <strong>Summary PDF</strong>. The Kanban view above is independent.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20"><Kanban className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.total || 0}</p>
              <p className="text-xs text-muted-foreground">Total Tasks</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><Clock className="w-5 h-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.by_status?.in_progress || 0}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20"><CheckCircle className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.by_status?.done || 0}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20"><Warning className="w-5 h-5 text-red-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.overdue || 0}</p>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20"><User className="w-5 h-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.my_tasks || 0}</p>
              <p className="text-xs text-muted-foreground">My Tasks</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Board Filter + View Mode Toggle */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="inline-flex rounded-md border border-input bg-card p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('kanban')}
            className={`px-3 py-1 text-xs font-medium rounded transition ${viewMode === 'kanban' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
            data-testid="view-kanban"
          >Kanban</button>
          <button
            type="button"
            onClick={() => setViewMode('completed')}
            className={`px-3 py-1 text-xs font-medium rounded transition ${viewMode === 'completed' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
            data-testid="view-completed"
          >Completed Tasks</button>
        </div>
        <Button
          variant={selectedBoard === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedBoard('all')}
        >
          All Tasks
        </Button>
        {boards.map(board => (
          <div key={board.id} className="flex items-center gap-1">
            <Button
              variant={selectedBoard === board.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedBoard(board.id)}
              style={{ borderColor: board.color }}
            >
              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: board.color }} />
              {board.name} ({board.task_count})
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleDeleteBoard(board.id)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>

      {/* Kanban Board (only when viewMode='kanban') */}
      {viewMode === 'kanban' && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map(column => (
          <div
            key={column.id}
            className="bg-card border border-border rounded-xl overflow-hidden"
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(column.id)}
          >
            <div className={`${column.color} px-4 py-3 flex items-center justify-between`}>
              <h3 className="font-semibold text-white">{column.title}</h3>
              <Badge variant="secondary" className="bg-white/20 text-white">
                {getTasksByStatus(column.id).length}
              </Badge>
            </div>
            
            <div className="p-3 space-y-3 min-h-[200px] max-h-[60vh] overflow-y-auto">
              <AnimatePresence>
                {getTasksByStatus(column.id).map(task => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    draggable
                    onDragStart={() => handleDragStart(task)}
                    onDragEnd={handleDragEnd}
                    className={`bg-background border border-border rounded-lg p-3 cursor-grab active:cursor-grabbing
                      ${draggedTask?.id === task.id ? 'opacity-50' : ''}
                      ${isOverdue(task.due_date) && task.status !== 'done' ? 'border-red-500/50' : ''}
                    `}
                    data-testid={`task-card-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm">{task.title}</h4>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <DotsThree className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditTask(task)}>
                            <PencilSimple className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteTask(task.id)} className="text-red-400">
                            <Trash className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {task.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-1 mt-2">
                      {task.tags?.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                      <div className="flex items-center gap-2">
                        <Badge className={`${getPriorityStyle(task.priority).bg} ${getPriorityStyle(task.priority).color} text-xs`}>
                          <Flag className="w-3 h-3 mr-1" />
                          {getPriorityStyle(task.priority).label}
                        </Badge>
                      </div>
                      {task.due_date && (
                        <span className={`text-xs ${isOverdue(task.due_date) && task.status !== 'done' ? 'text-red-400' : 'text-muted-foreground'}`}>
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    
                    {/* Assignee chip — always rendered. Resolves user-id to a
                        human-readable name via the in-memory users map so that
                        legacy tasks (where assigned_to_name was never mirrored)
                        no longer surface raw UUIDs to the user. */}
                    {(() => {
                      // Strict UUID v4 detector — never render a raw id to the user.
                      const looksLikeId = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
                      const rawName = (task.assigned_to_name || '').trim();
                      const fromUsers = task.assigned_to
                        ? ((users || []).find(x => x.id === task.assigned_to)?.name || '').trim()
                        : '';
                      const resolved = (rawName && !looksLikeId(rawName)) ? rawName
                        : (fromUsers && !looksLikeId(fromUsers)) ? fromUsers
                        : '';
                      return (
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <User className="w-3 h-3" />
                          {resolved || <span>—</span>}
                          {task.completed_at && (
                            <span className="ml-2 text-[10px] text-green-700 font-semibold">
                              ✓ {new Date(task.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {getTasksByStatus(column.id).length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No tasks
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Completed Tasks Table View */}
      {viewMode === 'completed' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="completed-tasks-table">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Completed Tasks
            </h3>
            <span className="text-xs text-muted-foreground">
              {tasks.filter(t => t.status === 'done').length} completed
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Board</th>
                  <th className="text-left px-4 py-2">Assignee</th>
                  <th className="text-left px-4 py-2">Priority</th>
                  <th className="text-left px-4 py-2">Completed By</th>
                  <th className="text-left px-4 py-2">Completed On</th>
                  <th className="text-left px-4 py-2">Due Date</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks
                  .filter(t => t.status === 'done')
                  .sort((a, b) => {
                    const ad = a.completed_at ? new Date(a.completed_at).getTime() : 0;
                    const bd = b.completed_at ? new Date(b.completed_at).getTime() : 0;
                    return bd - ad;
                  })
                  .map(task => {
                    const board = boards.find(b => b.id === task.board_id);
                    const looksLikeId = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
                    const rawName = (task.assigned_to_name || '').trim();
                    const fromUsers = task.assigned_to ? ((users.find(u => u.id === task.assigned_to)?.name) || '').trim() : '';
                    const assigneeName = (rawName && !looksLikeId(rawName)) ? rawName
                      : (fromUsers && !looksLikeId(fromUsers)) ? fromUsers
                      : '—';
                    const completedByName = (task.completed_by_name && !looksLikeId(task.completed_by_name)) ? task.completed_by_name : '—';
                    return (
                      <tr key={task.id} className="border-t border-border hover:bg-accent/30" data-testid={`completed-row-${task.id}`}>
                        <td className="px-4 py-2 font-medium">{task.title}</td>
                        <td className="px-4 py-2">
                          {board ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: board.color }} />
                              {board.name}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2">{assigneeName}</td>
                        <td className="px-4 py-2">
                          <Badge className={`${getPriorityStyle(task.priority).bg} ${getPriorityStyle(task.priority).color} text-xs`}>
                            {getPriorityStyle(task.priority).label}
                          </Badge>
                        </td>
                        <td className="px-4 py-2">{completedByName}</td>
                        <td className="px-4 py-2">
                          {task.completed_at ? new Date(task.completed_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-2">
                          {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditTask(task)} data-testid={`completed-edit-${task.id}`}>
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)} className="text-red-400" data-testid={`completed-delete-${task.id}`}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                {tasks.filter(t => t.status === 'done').length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      No completed tasks yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Edit Task' : 'Create Task'}</DialogTitle>
            <DialogDescription>Add task details and assign to team members</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Task title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Task description..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={taskForm.status} onValueChange={(v) => setTaskForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COLUMNS.map(col => (
                        <SelectItem key={col.id} value={col.id}>{col.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select value={taskForm.assigned_to} onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Board</Label>
                <Select value={taskForm.board_id || 'none'} onValueChange={(v) => setTaskForm(prev => ({ ...prev, board_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select board" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Board</SelectItem>
                    {boards.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {taskForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowTaskModal(false); resetTaskForm(); }}>Cancel</Button>
              <Button type="submit">{selectedTask ? 'Update' : 'Create'} Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Board Modal */}
      <Dialog open={showBoardModal} onOpenChange={(o) => { setShowBoardModal(o); if (!o) setEditingBoardId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBoardId ? 'Edit Board' : 'Create Board'}</DialogTitle>
            <DialogDescription>
              {editingBoardId
                ? 'Update the board name, description, and colour. Tasks already on this board keep their assignment.'
                : 'Create a new task board to organize your work'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateBoard}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Board Name *</Label>
                <Input
                  value={boardForm.name}
                  onChange={(e) => setBoardForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Product Development"
                  required
                  data-testid="board-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={boardForm.description}
                  onChange={(e) => setBoardForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Board description"
                  data-testid="board-desc-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Colour</Label>
                <div className="flex flex-wrap items-center gap-2">
                  {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#1e293b'].map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${boardForm.color === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setBoardForm(prev => ({ ...prev, color }))}
                      data-testid={`board-color-${color}`}
                      aria-label={`Choose color ${color}`}
                    />
                  ))}
                  {/* Custom HTML color picker for any colour the user wants */}
                  <input
                    type="color"
                    value={boardForm.color}
                    onChange={(e) => setBoardForm(prev => ({ ...prev, color: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer bg-transparent border border-input"
                    data-testid="board-color-custom"
                    title="Pick any custom colour"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{boardForm.color}</span>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowBoardModal(false); setEditingBoardId(null); }}>Cancel</Button>
              <Button type="submit" data-testid="save-board-btn">{editingBoardId ? 'Save Changes' : 'Create Board'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
